import { useMemo, useState, type ReactNode } from 'react';

import { Card, CardContent } from '@/ui/base/card';
import { DateRangePresetFilter } from '@/ui/DateRangePresetFilter';
import { type DateRangePreset, type DateRangeValue } from '@/ui/dateRangePreset';
import { EmptyState } from '@/ui/EmptyState';
import { GlossaryTerm } from '@/ui/GlossaryTerm';
import { PageHeader } from '@/ui/PageHeader';

import { useCenarioDeTela } from '../_dev/cenarioDeTela';
import { EstadoSimulado } from '../_dev/EstadoSimulado';

import { CicloFinanceiroConsolidado } from './_secoes/CicloFinanceiroConsolidado';
import { EvolucaoPorCompetencia } from './_secoes/EvolucaoPorCompetencia';
import { EvolucaoRevisoesAjustes } from './_secoes/EvolucaoRevisoesAjustes';
import { GovernancaPrazos } from './_secoes/GovernancaPrazos';
import { MixPorModalidade } from './_secoes/MixPorModalidade';
import { OperacaoImediata } from './_secoes/OperacaoImediata';
import { PipelineDePagamento } from './_secoes/PipelineDePagamento';
import { PipelineFinanceiro } from './_secoes/PipelineFinanceiro';
import { ReservaOrcamentariaSof } from './_secoes/ReservaOrcamentariaSof';
import { ResumoIndicadoresMes } from './_secoes/ResumoIndicadoresMes';
import { StatusPrazosEFunil } from './_secoes/StatusPrazosEFunil';
import { DADOS_PAINEL_EXECUTIVO } from './dados';

/**
 * `/operacao/financeiro/painel-executivo` — visão consolidada do programa
 * TEG: reserva orçamentária SOF, operação imediata, mix por modalidade,
 * ciclo financeiro, governança/prazos e evolução por competência.
 *
 * Dado simulado por decisão de produto (ver `../_dev/cenarioDeTela.ts`): a
 * API do módulo financeiro ainda não existe. Lido de `dados.ts` (que monta as
 * datas relativas a hoje): troque `DADOS_PAINEL_EXECUTIVO` e o
 * `EstadoSimulado` pela leitura da API, no mesmo `dados.ts` (padrão de
 * `modulos/admin/integracoes/dados.ts`) quando o módulo nascer na API.
 *
 * O filtro de período é decorativo por enquanto — muda o texto do subtítulo,
 * mas não refiltra a fixture (não há API ainda para recalcular os KPIs por
 * período). Vira funcional de verdade junto com `dados.ts`.
 */

const RÓTULO_PRESET: Partial<Record<DateRangePreset, string>> = {
  TODAY: 'hoje',
  LAST_7: 'últimos 7 dias',
  LAST_30: 'últimos 30 dias',
  THIS_MONTH: 'mês atual',
  LAST_MONTH: 'mês passado',
  THIS_YEAR: 'ano atual',
  ALL: 'todo o histórico',
};

function TituloSecao({ children, dica }: { children: ReactNode; dica?: ReactNode }) {
  return (
    <div className="mb-1 mt-2 flex items-baseline justify-between gap-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground/90">
        {children}
      </h2>
      {dica && <p className="text-xs text-muted-foreground">{dica}</p>}
    </div>
  );
}

/**
 * Legenda do glossário, igual ao protótipo. Os rótulos dentro de
 * `CartaoIndicador` (Conciliação, Cumprimento de prazos, Integridade da
 * auditoria, Glosas) não usam `GlossaryTerm` ali — o cartão inteiro já é um
 * `<Link>`, e o gatilho do glossário é um botão; aninhar botão dentro de link
 * quebra a semântica de foco e teclado (CLAUDE.md, regra 5). Aqui, fora do
 * link, é seguro.
 */
function LegendaDeTermos() {
  return (
    <Card className="rounded-2xl border-dashed bg-muted/30">
      <CardContent className="pb-4 pt-4 text-xs text-muted-foreground">
        <p className="mb-1 font-medium text-foreground/80">Termos técnicos</p>
        <p>
          Toque ou clique nos termos sublinhados (
          <GlossaryTerm termo="CONCILIACAO">Conciliação</GlossaryTerm>,{' '}
          <GlossaryTerm termo="CUMPRIMENTO_PRAZOS">Cumprimento de prazos</GlossaryTerm>,{' '}
          <GlossaryTerm termo="INTEGRIDADE_AUDIT">Integridade da auditoria</GlossaryTerm>,{' '}
          <GlossaryTerm termo="PIPELINE_PAGAMENTO">Pipeline de pagamento</GlossaryTerm>,{' '}
          <GlossaryTerm termo="GLOSAS">Glosas / devoluções</GlossaryTerm>) para ver a definição
          completa.
        </p>
      </CardContent>
    </Card>
  );
}

export function PainelExecutivoPage() {
  const cenario = useCenarioDeTela();
  const dados = cenario === 'sucesso' ? DADOS_PAINEL_EXECUTIVO : null;

  const [periodo, setPeriodo] = useState<DateRangeValue>({ preset: 'LAST_30' });

  const subtitulo = useMemo<ReactNode>(() => {
    const rotuloPeriodo = RÓTULO_PRESET[periodo.preset] ?? 'período selecionado';
    const alertas: ReactNode[] = [];
    const governanca = dados?.governancaPrazos;

    if (governanca && governanca.prazosVencidos > 0) {
      alertas.push(
        <span key="prz" className="font-medium text-destructive">
          {governanca.prazosVencidos} prazo(s) vencido(s)
        </span>,
      );
    }
    if (governanca && !governanca.integridadeOk) {
      alertas.push(
        <span key="int" className="font-medium text-destructive">
          auditoria com {governanca.integridadeQuebras} quebra(s)
        </span>,
      );
    }
    if (governanca && governanca.conciliacaoPendente > 0) {
      alertas.push(
        <span key="con" className="font-medium text-warning">
          {governanca.conciliacaoPendente} conciliação(ões) pendente(s)
        </span>,
      );
    }

    return (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>Visão consolidada para Gestor Financeiro DTP · {rotuloPeriodo}.</span>
        {alertas.length > 0 && (
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-muted-foreground">·</span>
            {alertas.map((alerta, indice) => (
              <span key={indice} className="inline-flex items-center gap-1">
                {indice > 0 && <span className="text-muted-foreground">·</span>}
                {alerta}
              </span>
            ))}
          </span>
        )}
      </span>
    );
  }, [periodo.preset, dados]);

  return (
    <div className="space-y-6">
      <PageHeader title="Painel Executivo Financeiro" description={subtitulo}>
        <DateRangePresetFilter
          label="Período"
          value={periodo}
          onChange={setPeriodo}
          width="w-full sm:w-[200px]"
          hideCustomRange
        />
      </PageHeader>

      <EstadoSimulado cenario={cenario} descricao="o painel executivo">
        {!dados ? (
          <EmptyState
            title="Nenhum indicador disponível"
            description="Os indicadores financeiros aparecem aqui quando houver movimento registrado no período."
          />
        ) : (
          <div className="space-y-6">
            <ResumoIndicadoresMes dados={dados.resumoMes} />

            <div>
              <TituloSecao dica="Saldo disponível por subfunção · integração SOF simulada">
                Reserva orçamentária SOF
              </TituloSecao>
              <ReservaOrcamentariaSof dados={dados.reservaSof} />
            </div>

            <OperacaoImediata dados={dados.operacaoImediata} reservaSof={dados.reservaSof} />

            <MixPorModalidade dados={dados.mixModalidade} />

            <EvolucaoRevisoesAjustes dados={dados.revisoesAjustesSerie} />

            <div>
              <TituloSecao dica="Competências e valores movimentados no período">
                Ciclo financeiro consolidado
              </TituloSecao>
              <div className="space-y-3">
                <CicloFinanceiroConsolidado dados={dados.cicloConsolidado} />
                <PipelineFinanceiro dados={dados.cicloFinanceiro} />
              </div>
            </div>

            <div>
              <TituloSecao dica="Conformidade institucional e integridade dos registros">
                Governança e prazos
              </TituloSecao>
              <div className="space-y-3">
                <GovernancaPrazos dados={dados.governancaPrazos} />
                <PipelineDePagamento dados={dados.cicloConsolidado} />
              </div>
            </div>

            <EvolucaoPorCompetencia dados={dados.evolucaoPorCompetencia} />

            <StatusPrazosEFunil
              statusPrazos={dados.statusPrazos}
              funilDocumentos={dados.funilDocumentos}
            />

            <LegendaDeTermos />
          </div>
        )}
      </EstadoSimulado>
    </div>
  );
}

export default PainelExecutivoPage;
